import React, { useState, useEffect } from "react";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./reactDatepickerOverrides.css";
import { format } from "date-fns";

interface GenericDateTimeRangeValue {
	start: Date | null;
	end: Date | null;
}

interface GenericDateTimeRangePickerProps {
	label?: string;
	value: GenericDateTimeRangeValue;
	onChange?: (value: GenericDateTimeRangeValue) => void;
	displayFormat?: string;
	disabled?: boolean;
	className?: string;
	singleDate?: boolean;
}

const GenericDateTimeRangePicker: React.FC<GenericDateTimeRangePickerProps> = ({
	label = "Date Range",
	value,
	onChange,
	displayFormat = "dd/MM/yyyy HH:mm",
	disabled = false,
	className = "",
	singleDate = false,
}) => {
	const [isOpen, setIsOpen] = useState(false);

	// Muestra "start - end" o solo "start" para singleDate
	const getDisplayValue = () => {
		if (singleDate) {
			return value.start ? format(value.start, displayFormat) : "";
		}
		if (!value.start && !value.end) return "";
		if (value.start && value.end) {
			return (
				format(value.start, displayFormat) +
				" - " +
				format(value.end, displayFormat)
			);
		}
		if (value.start) return format(value.start, displayFormat);
		if (value.end) return format(value.end, displayFormat);
		return "";
	};

	// Handle date range selection
	const handleDateChange = (dates: Date | [Date | null, Date | null] | null) => {
		if (singleDate) {
			// For single date, dates will be a single Date object
			const date = dates as Date;
			onChange?.({ start: date, end: null });
			setIsOpen(false); // Close picker after single date selection
		} else {
			// For range, dates will be an array
			const [start, end] = dates as [Date | null, Date | null];
			onChange?.({ start, end });
			// For range, only close if both dates are selected
			if (start && end) {
				setIsOpen(false);
			}
		}
	};

	// Close picker when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target as HTMLElement;
			if (!target.closest('.react-datepicker') && !target.closest('input')) {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isOpen]);

	return (
		<div className={`flex flex-col space-y-2 ${className}`}>
			{label && (
				<label className="text-sm font-medium text-semantic-text-secondary">
					{label}
				</label>
			)}

			<div className="relative">
				<input
					className={`
						w-full px-3 py-2 border border-semantic-border-secondary 
						rounded focus:outline-none focus:ring-2 focus:ring-semantic-text-brand 
						bg-semantic-bg-primary text-semantic-text-primary
						cursor-pointer
						${disabled ? "bg-gray-100 cursor-not-allowed" : ""}
					`}
					type="text"
					onClick={() => !disabled && setIsOpen(!isOpen)}
					value={getDisplayValue()}
					readOnly
					disabled={disabled}
					placeholder={singleDate ? "Select date..." : "Select date range..."}
				/>

				{isOpen && !disabled && (
					<div className="absolute z-50 mt-1 left-1/2 -translate-x-1/2 bg-white border border-gray-200 shadow-md rounded-md p-4">
						<ReactDatePicker
							selected={value.start}
							onChange={handleDateChange}
							startDate={value.start}
							endDate={singleDate ? null : value.end}
							selectsRange={!singleDate}
							inline
							monthsShown={1}
							showTimeInput={!singleDate}
							timeFormat="hh:mm aa"
							dateFormat={displayFormat}
							calendarClassName="my-react-datepicker"
						/>
					</div>
				)}
			</div>
		</div>
	);
};

export default GenericDateTimeRangePicker;
